import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '../common/Tooltip';
import { REC_TYPE_META, PRIORITY_META, HEALTH_BAND_META, SNOOZE_OPTIONS, effectiveStatus, recommendationEvidenceHref } from '../../utils/recommendations';

const formatTs = (ts) => (ts?.toDate ? ts.toDate().toLocaleDateString() : '');

/**
 * One recommendation. All AI/rule text is rendered as plain text (never HTML).
 * `compact` renders a single scannable row; rationale and evidence expand on demand.
 */
const RecommendationCard = ({ rec, canAct, busy, showCustomer = false, compact = false, onAccept, onDismiss, onSnooze, onComplete, onViewTab }) => {
  const [showWhy, setShowWhy] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const type = REC_TYPE_META[rec.type] || { label: rec.type, icon: '💡' };
  const priority = PRIORITY_META[rec.priority] || PRIORITY_META.low;
  const status = effectiveStatus(rec);
  const isOpen = status === 'proposed' || status === 'snoozed';
  const action = rec.suggestedAction || {};
  const btn = compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  const hasEvidence = Array.isArray(rec.evidence) && rec.evidence.length > 0;

  const statusPills = (
    <>
      {status === 'snoozed' && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 whitespace-nowrap">Snoozed until {formatTs(rec.snoozeUntil)}</span>
      )}
      {!isOpen && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 capitalize">{status}</span>
      )}
    </>
  );

  const evidenceList = hasEvidence && (
    <ul className="mt-2 space-y-1">
      {rec.evidence.map((e, i) => {
        const href = recommendationEvidenceHref(e);
        return (
          <li key={`${e.refId || 'ev'}-${i}`} className="text-xs text-gray-600 flex items-start gap-2 min-w-0">
            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 flex-shrink-0">{e.kind}</span>
            {href ? <Link to={href} className="text-indigo-600 hover:underline break-words">{e.fact}</Link> : <span className="break-words">{e.fact}</span>}
          </li>
        );
      })}
    </ul>
  );

  const outcome = (
    <>
      {rec.resultRef?.type === 'task' && (
        <div className="mt-2 text-xs text-green-700">
          ✓ Task created: <Link to={`/tasks/${encodeURIComponent(rec.resultRef.id)}`} className="underline">{rec.resultRef.id}</Link>
          {rec.actedByName ? ` by ${rec.actedByName}` : ''}
        </div>
      )}
      {status === 'dismissed' && rec.feedback?.reasonCode && (
        <div className="mt-2 text-xs text-gray-500">Dismissed by {rec.actedByName || 'user'}: {rec.feedback.reasonCode.replace(/_/g, ' ')}{rec.feedback.reason ? ` — ${rec.feedback.reason}` : ''}</div>
      )}
    </>
  );

  const actions = canAct && isOpen && (
    <div className={`flex flex-wrap items-center gap-2 ${compact ? '' : 'mt-3'}`}>
      {action.kind === 'create_task' && (
        <Tooltip text="Review and create the suggested task (you can edit everything first)">
          <button type="button" disabled={busy} onClick={() => onAccept(rec)} className={`${btn} rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap`}>
            Create Task
          </button>
        </Tooltip>
      )}
      {action.kind === 'view_tab' && onViewTab && (
        <Tooltip text={`Open the ${action.payload?.tab} tab to review`}>
          <button type="button" onClick={() => onViewTab(action.payload?.tab)} className={`${btn} rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 whitespace-nowrap`}>
            Review {action.payload?.tab}
          </button>
        </Tooltip>
      )}
      {action.kind === 'view_tab' && (
        <Tooltip text="Mark as done once you've handled it">
          <button type="button" disabled={busy} onClick={() => onComplete(rec)} className={`${btn} rounded border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50 whitespace-nowrap`}>
            Mark Done
          </button>
        </Tooltip>
      )}
      <div className="relative">
        <Tooltip text="Hide this for a while; it comes back if still relevant">
          <button type="button" disabled={busy} onClick={() => setShowSnooze(v => !v)} className={`${btn} rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap`}>
            Snooze ▾
          </button>
        </Tooltip>
        {showSnooze && (
          <div className="absolute right-0 z-20 mt-1 bg-white border border-gray-200 rounded shadow-lg">
            {SNOOZE_OPTIONS.map(d => (
              <button key={d} type="button" onClick={() => { setShowSnooze(false); onSnooze(rec, d); }} className="block w-full text-left px-4 py-1.5 text-sm hover:bg-gray-100 whitespace-nowrap">
                {d} days
              </button>
            ))}
          </div>
        )}
      </div>
      <Tooltip text="Not useful — tell us why so recommendations improve">
        <button type="button" disabled={busy} onClick={() => onDismiss(rec)} className={`${btn} rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50`}>
          Dismiss
        </button>
      </Tooltip>
    </div>
  );

  if (compact) {
    return (
      <div className="px-3 py-2 bg-white min-w-0">
        <div className="flex flex-col md:flex-row md:items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            aria-expanded={expanded}
            title={expanded ? 'Hide details' : 'Show rationale and evidence'}
            className="flex items-center gap-2 min-w-0 flex-1 text-left"
          >
            <span className="text-gray-400 text-xs w-3 flex-shrink-0" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
            <span aria-hidden="true" title={type.label}>{type.icon}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${priority.className}`}>{priority.label}</span>
            <span className={`text-sm text-gray-900 min-w-0 ${expanded ? 'break-words' : 'truncate'}`}>{rec.title}</span>
          </button>
          <div className="flex flex-wrap items-center gap-2 md:flex-shrink-0 pl-5 md:pl-0">
            {statusPills}
            {actions}
          </div>
        </div>
        {expanded && (
          <div className="pl-5 mt-1">
            <div className="text-xs uppercase tracking-wide text-gray-500">{type.label}</div>
            <p className="text-sm text-gray-700 mt-0.5 break-words">{rec.rationale}</p>
            {evidenceList}
          </div>
        )}
        <div className="pl-5">{outcome}</div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white min-w-0">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="text-lg" aria-hidden="true">{type.icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{type.label}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priority.className}`}>{priority.label}</span>
        {statusPills}
        {showCustomer && rec.healthBand && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${HEALTH_BAND_META[rec.healthBand] || 'bg-gray-100 text-gray-700'}`}>{rec.healthBand}</span>
        )}
      </div>

      {showCustomer && (
        <Link to={`/customer/${encodeURIComponent(rec.customerId)}?tab=recommendations`} className="text-sm font-medium text-indigo-600 hover:underline break-words">
          {rec.customerName || rec.customerId}
        </Link>
      )}

      <h4 className="font-semibold text-gray-900 break-words">{rec.title}</h4>
      <p className="text-sm text-gray-700 mt-1 break-words">{rec.rationale}</p>

      {hasEvidence && (
        <div className="mt-2">
          <button type="button" onClick={() => setShowWhy(v => !v)} className="text-xs text-indigo-600 hover:underline">
            {showWhy ? 'Hide evidence' : `Why? (${rec.evidence.length})`}
          </button>
          {showWhy && evidenceList}
        </div>
      )}

      {outcome}
      {actions}
    </div>
  );
};

export default RecommendationCard;
